/* eslint-disable indent */

import React, {
    useEffect,
    useState,
    useRef,
    useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import Cropper from 'react-easy-crop';
import { FaFileExport } from 'react-icons/fa';
import { BsCheck } from 'react-icons/bs';
import {
    dateStringDot,
    hasAccess,
} from '../../../utils';
import { getCase, getFolderCase } from '../../../actions/case';
import { DatePicker } from '../../../lib/HooksFormFields';
import { cropFile, updateDate } from '../../../actions/file';
import getCroppedImg from './cropImage';
import PositionalModal from '../../../lib/PositionalModal';
import ModalStandalone from '../../../lib/ModalStandalone';
import MoveImageToDocumentModal from '../../MoveImageToDocumentModal';
import dots from '../../../assets/images/icons/green-dots.svg';
import mailIcon from '../../../assets/images/icons/mail.svg';
import deleteIcon from '../../../assets/images/icons/delete.svg';
import moveFolderIcon from '../../../assets/images/icons/folder-move-to.svg';

import styles from './image-item.module.scss';

const ImageItem = ({
    file,
    selected,
    handleSelect,
    actions = {},
}) => {
    const { user } = useSelector((store) => store).authReducer;
    const { selectedCase } = useSelector((store) => store).caseReducer;
    const [searchParams] = useSearchParams();
    const modalRef = useRef();
    const modalParent = useRef();
    const modalImage = useRef();
    const moveModalRef = useRef();
    const dispatch = useDispatch();
    const [preview, setPreview] = useState();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [rotation, setRotation] = useState(0);
    const [editFile, setEditFile] = useState(false);

    const { control, getValues } = useForm();

    useEffect(() => {
        if (preview) modalImage.current.open();
    }, [preview]);

    function openModal(e) {
        e.preventDefault();
        e.stopPropagation();
        modalRef.current.open();
    }

    function sendFile(e) {
        e.preventDefault();
        e.stopPropagation();
        modalRef.current.close();
        actions.send({ _id: file._id, name: file.name, type: 'file' });
    }

    function deleteFile(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!actions.delete || typeof actions.delete !== 'function') return;
        actions.delete(file);
    }

    async function dowloadFile(e) {
        e.preventDefault();
        e.stopPropagation();
        const config = {
        headers: {
            Authorization: `${localStorage.getItem('token')}`,
            agency: `${localStorage.getItem('agency')}`,
        },
        };
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/file/${file._id}`, config);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${file.name}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            console.log(err);
        }
    }

    function toggleEditFile(e) {
        e.preventDefault();
        e.stopPropagation();
        setEditFile((state) => !state);
    }

    function openMoveModal(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!moveModalRef.current?.open) return;
        moveModalRef.current.open();
        modalRef.current.close();
    }

    const getUrlImg = useMemo(() => {
        const token = localStorage.getItem('token');
        return `${process.env.REACT_APP_API_URL}/static/${file?.path.replace('upload/', '')}?token=${token}`;
    }, [file]);

    const getUrlImgCropped = useMemo(() => {
        if (!file?.pathCropped) return null;
        const token = localStorage.getItem('token');
        return `${process.env.REACT_APP_API_URL}/static/${file?.pathCropped.replace('upload/', '')}?token=${token}`;
    }, [file]);

    const isMovable = () => {
        const inCasePictures = selectedCase?.pictures?.some(
            (p) => (p.file?._id || p.file)?.toString() === file._id?.toString(),
        );
        if (inCasePictures) return true;
        const inTask = selectedCase?.tasks?.some((t) => (
            t.files?.some((f) => (f._id || f)?.toString() === file._id?.toString())
        ));
        return !!inTask;
    };

    const onCropComplete = (croppedArea, croppedAreaReceived) => {
        setCroppedAreaPixels(croppedAreaReceived);
    };

    async function updateDateFile(date) {
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0));

        await updateDate({ date: utcDate.toISOString() }, file._id);
    }

    const handleCrop = async () => {
        try {
            const croppedImage = await getCroppedImg(
              getUrlImg,
              croppedAreaPixels,
              rotation,
            );
            // Transformer l'URL blob en un vrai Blob
            const response = await fetch(croppedImage);
            const blob = await response.blob();

            const formData = new FormData();
            formData.append('file', blob, 'croppedImage.jpg');
            const cropResponse = await cropFile(formData, file._id);

            // Recharger le dossier pour mettre à jour l'image
            if (cropResponse?.status === 200 || cropResponse?.status === 201) {
                const selectedDate = getValues('date');
                if (selectedDate) {
                    await updateDateFile(selectedDate);
                }
                // Recharger le dossier courant pour mettre à jour l'image
                if (selectedCase) {
                    if (searchParams.get('folder')) {
                        await getFolderCase(dispatch, searchParams.get('folder'));
                    } else {
                        await getCase(dispatch, selectedCase._id);
                    }
                }
            }

            setPreview(false);
            setEditFile(false);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
            setRotation(0);
            setEditFile(false);
          } catch (e) {
            console.error(e);
          }
    };

    function getDefaultDate() {
        if (file.date) return new Date(file.date);
        if (file.createdAt) return new Date(file.createdAt);
        return null;
    }

    return (
        <li className={styles.imageItem}>
        <ModalStandalone
            ref={modalImage}
            handleClose={() => {
                setEditFile(false);
                setPreview(false);
                setCrop({ x: 0, y: 0 });
                setZoom(1);
                setCroppedAreaPixels(null);
                setRotation(0);
                setEditFile(false);
            }}
        >
            <div className={styles.modalImage}>
                {editFile ? (
                    <>
                        <div className={styles.datePicker}>
                            <DatePicker
                                name='date'
                                control={control}
                                dateFormat='eeee dd LLLL yyyy'
                                placeholder='Date du fichier'
                                label='Date du fichier'
                                className='discreet'
                                inline
                                defaultValue={getDefaultDate()}
                                handleChange={(date) => updateDateFile(date)}
                            />
                        </div>
                        <div className={styles.cropperContainer}>
                            <Cropper
                                image={getUrlImg}
                                crop={crop}
                                zoom={zoom}
                                rotation={rotation}
                                aspect={1 / 1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </div>
                        <div className={styles.cropperActions}>
                            <button onClick={() => setRotation(rotation + 90)}>
                                Rotation à droite
                            </button>
                            <button onClick={() => handleCrop()}>
                                Sauvegarder l'image
                            </button>
                        </div>
                    </>
                ) : (
                    <div className={styles.imageContainer}>
                        <a href={getUrlImgCropped || getUrlImg} target='_blank' rel='noopener noreferrer'>
                            <img
                                src={getUrlImgCropped || getUrlImg}
                                alt={file.name}
                            />
                        </a>
                        {hasAccess(user.role) && (
                            <div className={styles.editFile}>
                                <button onClick={toggleEditFile}>
                                    Modifier l'image
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </ModalStandalone>
        <PositionalModal
            ref={modalRef}
            parent={modalParent}
            alignmentX={'right'}
            alignmentY={'middle'}
        >
            <div className={styles.containerActions} data-close-modal='false' onClick={(e) => e.stopPropagation()}>
            {(hasAccess(user.role) && isMovable() && actions.movePictureToNumber && actions.getSourceForFile && searchParams.get('pictures')) && (
                <div className={styles.moveAction} onClick={openMoveModal}>
                    <img src={moveFolderIcon} alt="Déplacer vers un numéro de document" />
                    <p>Déplacer vers un numéro de document</p>
                </div>
            )}
            {actions.print && (<div onClick={(e) => dowloadFile(e)}>
                <FaFileExport size='20px' />
                <p>Télécharger</p>
            </div>
            )}
            {actions.send && (<div onClick={(e) => sendFile(e)}><img src={mailIcon} alt='Envoyer par mail' /><p>Envoyer par mail</p></div>)}
            {(hasAccess(user.role) && actions.delete) && (
                <div onClick={(e) => deleteFile(e)}>
                <img src={deleteIcon} alt='Supprimer' />
                <p>Supprimer</p>
                </div>
            )}
            </div>
        </PositionalModal>

        {(hasAccess(user.role) && isMovable() && actions.movePictureToNumber && actions.getSourceForFile && searchParams.get('pictures')) && (
            <MoveImageToDocumentModal
                ref={moveModalRef}
                fileId={file._id}
                pictureType={searchParams.get('pictures')}
                getSourceForFile={actions.getSourceForFile}
                onSubmitTransfer={actions.movePictureToNumber}
            />
        )}
        <div className={styles.content}>
            <div
            style={{ cursor: 'pointer' }}
            onClick={() => {
                setPreview(true);
            }
            }>
            <img src={getUrlImgCropped || getUrlImg} alt={`dossier ${file.name}`} />
            </div>
            <p>{file.name}<br/>{file.title}</p>
            <div className={styles.details}>
            <div className={styles.date}>
                {getDefaultDate() && dateStringDot(getDefaultDate())}
            </div>
            <div className={styles.toggle}>
                <span onClick={openModal} ref={modalParent}>
                <img src={dots} alt={`action sur le dossier: ${file.name}`} />
                </span>
            </div>
            <Checkbox checked={selected} handleClick={() => handleSelect(file._id, 'image')}/>
            </div>
        </div>
        </li>
    );
};

const Checkbox = (({ checked, handleClick }) => {
    useEffect(() => {}, []);
    return (
        <div className={styles.checkbox} onClick={() => handleClick()}>
        {checked && <BsCheck />}
        </div>
    );
});

export default ImageItem;
